import { useState, FormEvent } from 'react'
import { useUser } from '@hooks/user'

import { NextSeo } from 'next-seo'
import { Header } from '@components/Header'
import { Footer } from '@components/Footer'
import { Button } from '@components/UI/Button'
import { Label } from '@components/UI/Form/Label'
import { Input } from '@components/UI/Form/Input'
import { Spinner } from '@components/UI/Spinner'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { CheckCircleIcon } from '@heroicons/react/outline'

export default function Login() {
  const { login } = useUser()
  const { query, push } = useRouter()
  const [error, setError] = useState('')

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()

    async function attemptLogin(email: string, password: string) {
      try {
        await login.mutateAsync({ email, password })

        const reportId =
          localStorage.getItem('current-report') ??
          localStorage.getItem('default-report')
        if (reportId) {
          void push(`/domoreport?report=${reportId}`)
        } else {
          void push('/domoreport')
        }
      } catch (error) {
        if (error.response.status === 404) {
          setError('Your email or password is incorrect')
        }
      }
    }

    const data = new FormData(e.currentTarget)
    const email = data.get('email') as string
    const password = data.get('password') as string

    void attemptLogin(email, password)
  }

  return (
    <>
      <NextSeo
        title="Log In"
        description="New to Majordomo? Local and accurate Home inspection repair estimates in 24-hours or less. 24-hour Instant chat, log-in Create an Account, Forgot Password"
      />
      <Header />

      <main className="pt-8 sm:px-0 px-8 container mx-auto">
        <div className="flex md:flex-row flex-col justify-between items-center lg:pb-36 pb-12">
          <img
            src="/images/product-pages/login-hero.png"
            className="md:inline hidden order-first mb-0 w-1/2"
            width="50%"
          />

          <div className="flex flex-col md:items-start items-center justify-center w-full 2xl:p-36 xl:p-20 md:p-8 md:order-last order-first">
            <h1 className="font-black tracking-tight mb-8 text-3xl text-center w-full">
              Log In
            </h1>

            {query.ref === 'password-reset-success' && (
              <div className="flex items-center p-4 mb-4 rounded bg-green-300 w-full">
                <CheckCircleIcon
                  width={24}
                  height={24}
                  className="mr-1 text-green-800"
                />
                <p className="text-green-800">Your password has been reset. </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="w-full">
              <section className="mb-4">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  name="email"
                  type="email"
                  required
                  placeholder="example@example.com"
                />
              </section>
              <section className="mb-8">
                <Label htmlFor="login-password">Password</Label>
                <Input
                  id="login-password"
                  name="password"
                  type="password"
                  required
                />
              </section>
              {error && (
                <p className="mb-8 text-red-400 text-center">{error}</p>
              )}
              <section className="flex justify-start">
                {login.status === 'loading' ? (
                  <Spinner />
                ) : (
                  <Button
                    variant="secondary"
                    clipSide="right"
                    data-testid="loginSubmit"
                    wide
                  >
                    Log In
                  </Button>
                )}
              </section>
            </form>

            <div className="flex flex-col items-center w-full mt-12">
              <p>
                New to Majordomo?{' '}
                <Link href="/signup">
                  <a className="font-black text-accentBlue hover:underline">
                    Create an account
                  </a>
                </Link>
              </p>
              <Link href="/reset-password">
                <a className="font-black text-accentBlue hover:underline">
                  Forgot Password?
                </a>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  )
}
